import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Search as SearchIcon, Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";

export interface CardSelectorItem {
  id: number | string;
  name: string;
  description?: string;
  category?: string;
  color?: string;
  icon?: React.ElementType;
}

interface CardSelectorProps {
  items: CardSelectorItem[];
  selectedItems: CardSelectorItem[];
  onChange: (items: CardSelectorItem[]) => void;
  label: string;
  searchPlaceholder?: string;
  loading?: boolean;
  categories?: Record<string, { name: string; color: string; textColor: string }>;
  multiSelect?: boolean;
  onSearchChange?: (searchTerm: string) => void;
}

export default function CardSelector({
  items,
  selectedItems,
  onChange,
  label,
  searchPlaceholder = "Search...",
  loading = false,
  categories = {},
  multiSelect = true,
  onSearchChange,
}: CardSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const isSelected = (item: CardSelectorItem) =>
    selectedItems.some((selected) => selected.id === item.id);

  const handleToggle = (item: CardSelectorItem) => {
    if (multiSelect) {
      if (isSelected(item)) {
        onChange(selectedItems.filter((selected) => selected.id !== item.id));
      } else {
        onChange([...selectedItems, item]);
      }
    } else {
      // Single select mode
      if (isSelected(item)) {
        onChange([]);
      } else {
        onChange([item]);
      }
    }
  };

  const handleRemove = (item: CardSelectorItem) => {
    onChange(selectedItems.filter((selected) => selected.id !== item.id));
  };

  const getItemColor = (item: CardSelectorItem) => {
    if (item.color) return item.color;
    if (item.category && categories[item.category]) {
      return categories[item.category].color;
    }
    return "#5D4E37";
  };

  const getItemTextColor = (item: CardSelectorItem) => {
    if (item.category && categories[item.category]) {
      return categories[item.category].textColor;
    }
    return "#ffffff";
  };

  const ItemIcon = (item: CardSelectorItem) => {
    if (item.icon) return <item.icon sx={{ fontSize: 32 }} />;
    return null;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {label}
      </Typography>

      {/* Search */}
      <TextField
        fullWidth
        placeholder={searchPlaceholder}
        value={searchTerm}
        onChange={(e) => handleSearchChange(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
        }}
        sx={{ mb: 2 }}
      />

      {/* Items Grid */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? "No results found" : "No items available"}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {items.map((item) => {
            const selected = isSelected(item);
            const color = getItemColor(item);
            const textColor = getItemTextColor(item);

            return (
              <Chip
                key={item.id}
                label={item.name}
                onClick={() => handleToggle(item)}
                deleteIcon={
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(item);
                    }}
                    sx={{ color: selected ? textColor : color }}
                  >
                    {selected ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                  </IconButton>
                }
                onDelete={() => {}}
                sx={{
                  border: `2px solid ${color}`,
                  backgroundColor: selected ? color : "transparent",
                  color: selected ? textColor : color,
                  fontWeight: "bold",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 2,
                  },
                  "& .MuiChip-deleteIcon": {
                    color: selected ? textColor : color,
                    marginLeft: 0,
                  },
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
